import { sumBy } from "lodash-es";
import isNull from "lodash-es/isNull";

import { apiClient } from "../../common/apiClient";
import { platformRoutes, resolvePlatformRequestUrl } from "../../common/platformRoutes";
import type {
   DSPOrderList,
   M3Order,
   M3OrderError,
   M3OrderListed,
   OrderFilter,
   OrderFilterQueryParams,
   OrderStatusFilterValue
} from "../../common/types/m3Types";
import type { DeliveryNotesInfo } from "../../common/types/productOrderTypes";
import { formatM3Date } from "../../common/utils/dateUtils";
import { newOrderStatusOptions } from "../../common/utils/searchOptions";
import theme from "../../themes/theme";
import authStore from "../auth/authStore";

const buildBaseOrderQueryParams = (filters: OrderFilter): OrderFilterQueryParams => {
   const filterQueryParams: OrderFilterQueryParams = {
      typeOfOrder: "K"
   };

   if (filters.orderNumber.trim().length > 0) {
      // If searching for order number we don't care about the other filters
      filterQueryParams.orderNumber = filters.orderNumber.trim();
      return filterQueryParams;
   }

   if (filters.orderName.trim().length > 0) {
      filterQueryParams.orderName = filters.orderName.trim();
   }
   if (!isNull(filters.dateRange.from)) {
      filterQueryParams.fromDate = formatM3Date(filters.dateRange.from);
   }
   if (!isNull(filters.dateRange.to)) {
      filterQueryParams.toDate = formatM3Date(filters.dateRange.to);
   }
   if (!isNull(filters.dateRange.from) && isNull(filters.dateRange.to)) {
      // If only one date is selected in the date range, use this date as both start and end date
      filterQueryParams.toDate = formatM3Date(filters.dateRange.from);
   }

   return filterQueryParams;
};

export const convertOrderFilterToQueryParams = (filters: OrderFilter): OrderFilterQueryParams => {
   const filterQueryParams = buildBaseOrderQueryParams(filters);

   if (filterQueryParams.orderNumber === undefined && filters.status !== "") {
      const selectedOption = newOrderStatusOptions.find((option) => option.value === filters.status);
      if (selectedOption && selectedOption.statuses.length > 0) {
         filterQueryParams.status = selectedOption.statuses;
      }
   }

   return filterQueryParams;
};

const legacyStatusRangeByValue: Partial<Record<OrderStatusFilterValue, [string, string]>> = {
   RECEIVED: ["20", "33"],
   PICKED: ["33", "44"],
   PACKED: ["44", "88"],
   INVOICED: ["77", "79"],
   DELETED: ["90", "90"]
};

export const convertOrderFilterToLegacyQueryParams = (filters: OrderFilter): OrderFilterQueryParams => {
   const filterQueryParams = buildBaseOrderQueryParams(filters);

   if (filterQueryParams.orderNumber === undefined && filters.status !== "") {
      const legacyRange = legacyStatusRangeByValue[filters.status];
      if (legacyRange) {
         filterQueryParams.fromStatus = legacyRange[0];
         filterQueryParams.toStatus = legacyRange[1];
      }
   }

   return filterQueryParams;
};

export const fetchOrderListFromM3 = async (
   companyNumber: string,
   customerNumber: string,
   filters: OrderFilter
): Promise<M3OrderListed[]> => {
   const queryParams = convertOrderFilterToLegacyQueryParams(filters);
   const customerOrderListUrl = resolvePlatformRequestUrl(platformRoutes.customerOrderList, { storeId: theme.storeId });

   return await apiClient(customerOrderListUrl, authStore.getSessionToken())
      .query({ companyNumber, customerNumber, ...queryParams })
      .get()
      .json();
};

export const fetchDSPOrderList = async (
   companyNumber: string,
   customerNumber: string,
   filters: OrderFilter
): Promise<DSPOrderList[]> => {
   const queryParams = convertOrderFilterToQueryParams(filters);
   const customerOrderListUrl = resolvePlatformRequestUrl(platformRoutes.customerDSPOrderList, { storeId: theme.storeId });

   return await apiClient(customerOrderListUrl, authStore.getSessionToken())
      .query({ companyNumber, customerNumber, ...queryParams })
      .get()
      .json();
};

export const fetchOrderDetailsFromM3 = async (
   companyNumber: string,
   customerNumber: string,
   orderNumber: string
): Promise<M3Order | M3OrderError> => {
   const customerOrderUrl = resolvePlatformRequestUrl(platformRoutes.customerOrder, { storeId: theme.storeId });

   return await apiClient(customerOrderUrl, authStore.getSessionToken())
      .query({ companyNumber, customerNumber, orderNumber })
      .get()
      .json();
};

export const fetchDeliveryNotes = async (
   orderNumber: string,
   customerNumber: string,
   companyNumber: string,
   division: string
): Promise<DeliveryNotesInfo> => {
   const customerDeliveryNotesUrl = resolvePlatformRequestUrl(platformRoutes.customerDeliveryNotes, {
      customerNumber,
      orderNumber
   });

   return await apiClient(customerDeliveryNotesUrl, authStore.getSessionToken()).query({ companyNumber, division }).get().json();
};

// Uses the Intl functionality in the ES6 standard to generate a comma-separated list with correct "og" placement.
export const norwegianListFormatter =
   typeof Intl !== "undefined" && typeof Intl.ListFormat !== "undefined"
      ? new Intl.ListFormat("no-NO", { style: "short", type: "conjunction" })
      : {
           format: (items: string[]) => items.join(", ")
        };

const currencyFormat =
   typeof Intl !== "undefined" && typeof Intl.NumberFormat !== "undefined"
      ? new Intl.NumberFormat("nb-NO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : {
           format: (value: number) => value.toFixed(2).replace(".", ",")
        };

export const createReadableDiscountInformation = (
   appliedPromotionInformation: OrderLineAppliedPromotion[],
   totalDiscountAmount: number
) => {
   const discountFromVoucherify = sumBy(appliedPromotionInformation, "appliedDiscount");
   const discountFromM3 = totalDiscountAmount - discountFromVoucherify;
   const numberOfDiscounts = appliedPromotionInformation.length + (discountFromM3 > 0 ? 1 : 0);
   const label = numberOfDiscounts === 1 ? "Kampanje" : "Kampanjer";
   return `${label}: kr ${currencyFormat.format(-discountFromVoucherify - discountFromM3)}`;
};

export const generateRandomOrderId = (): string => Math.random().toString(36).substring(2, 8);

export type OrderLineAppliedPromotion = {
   appliedDiscount: number;
   discountName: string;
   discountId: string;
};

export const getRecyclingChargeFromM3 = (order: M3Order): number => {
   return sumBy(order.orderLines, "lineRecyclingCharge");
};
